import { call, put, select, takeLatest } from "redux-saga/effects";

import * as campaigns from "middleware/campaigns";
import { types } from "reducers/campaigns";

const sagas = [
    takeLatest(types.LOAD_CAMPAIGN_REQUEST, loadCampaign),
    takeLatest(types.LOAD_CAMPAIGN_LIST_REQUEST, loadCampaignList),
    takeLatest(types.LOAD_CAMPAIGN_IMAGE, loadCampaignImage),
    takeLatest(types.LOAD_CAMPAIGN_IMAGES, loadCampaignImages),
    takeLatest(types.REMOVE_CAMPAIGNS, removeCampaigns),
    takeLatest(types.DISMISS_CAMPAIGN_PERMANENT_REQUEST, dismissPermanentCampaign),
];

export default sagas;

const DEFAULT_IMAGE_MIME = "image/png";
const ALLOWED_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

function sanitizeImageMimeType(contentType) {
    if (!contentType || typeof contentType !== "string") {
        return DEFAULT_IMAGE_MIME;
    }

    const mimeType = contentType.toLowerCase().trim();
    return ALLOWED_IMAGE_MIME_TYPES.includes(mimeType) ? mimeType : DEFAULT_IMAGE_MIME;
}

function normalizeBase64(value) {
    if (!value || typeof value !== "string") {
        return "";
    }

    return value.replace(/\s/g, "");
}

function isValidBase64(value) {
    if (!value || typeof value !== "string" || value.length % 4 !== 0) {
        return false;
    }

    return /^[A-Za-z0-9+/]+={0,2}$/.test(value);
}

function base64ToBlob(base64Content, mimeType) {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
}

function createLocalImageUrl(base64Content, mimeType) {
    const dataUrl = `data:${mimeType};base64,${base64Content}`;

    if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
        return dataUrl;
    }

    try {
        const blob = base64ToBlob(base64Content, mimeType);
        return URL.createObjectURL(blob);
    } catch (err) {
        return dataUrl;
    }
}

function revokeLocalImageUrl(imageUrl) {
    if (!imageUrl || typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function") {
        return;
    }

    if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
    }
}

function revokeCampaignItemResources(item) {
    if (!item) {
        return;
    }

    if (item.imageUrl) {
        revokeLocalImageUrl(item.imageUrl);
    }

    if (item.imageUrls && Array.isArray(item.imageUrls)) {
        item.imageUrls.forEach((url) => revokeLocalImageUrl(url));
    }
}

function* loadCampaign({ section, isAnonymous, imageFormat }) {
    try {
        const response = yield call(isAnonymous ? campaigns.fetchAnonymousCampaign : campaigns.fetchCampaign, section);
        if (response.status !== 304) {
            const { data } = response.data;
            const normalizedImageFormat = (imageFormat || "url").toLowerCase();

            yield put({
                type: normalizedImageFormat === "base64" ? types.LOAD_CAMPAIGN_IMAGE : types.LOAD_CAMPAIGN_IMAGES,
                item: data.campaign,
            });
        }
    } catch (err) {
        yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
    }
}

function* loadCampaignList({ section, isAnonymous }) {
    try {
        const response = yield call(
            isAnonymous ? campaigns.fetchAnonymousActiveCampaigns : campaigns.fetchActiveCampaigns,
            section,
        );
        if (response.status !== 304) {
            const { data } = response.data;
            const items = Array.isArray(data.campaigns) ? data.campaigns : [];

            if (items.length === 0) {
                yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
                return;
            }

            const fittingImageDisplay = getFittingImageDisplay();
            const preparedItems = [];
            for (let index = 0; index < items.length; index += 1) {
                const preparedItem = yield call(prepareCampaignWithImages, items[index], fittingImageDisplay);
                if (preparedItem) {
                    preparedItems.push(preparedItem);
                }
            }

            if (preparedItems.length === 0) {
                yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
                return;
            }

            const currentItems = yield select((state) => state.campaigns.items || []);
            currentItems.forEach((campaignItem) => revokeCampaignItemResources(campaignItem));

            yield put({
                type: types.LOAD_CAMPAIGN_SUCCESS,
                item: preparedItems[0],
                items: preparedItems,
            });
        }
    } catch (err) {
        yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
    }
}

function getFittingImage([mobileImage, tabletImage, desktopImage]) {
    if (window.innerWidth < 769) {
        return mobileImage;
    }
    if (window.innerWidth < 993) {
        return tabletImage;
    }
    return desktopImage;
}

function getFittingImageDisplay() {
    if (typeof window === "undefined" || typeof window.innerWidth !== "number") {
        return "NORMAL";
    }

    if (window.innerWidth < 769) {
        return "MOBILE";
    }
    if (window.innerWidth < 993) {
        return "NARROW";
    }
    return "NORMAL";
}

function matchesImageDisplay(image, display) {
    return (
        image &&
        image.idImage &&
        image.imageSize &&
        image.imageSize.display &&
        image.imageSize.display.toUpperCase() === display
    );
}

function* prepareCampaignWithImages(campaignItem, fittingImageDisplay) {
    const imageList = Array.isArray(campaignItem.imageList)
        ? campaignItem.imageList.filter((image) => matchesImageDisplay(image, fittingImageDisplay))
        : [];
    if (imageList.length === 0) {
        return null;
    }

    const imageUrls = [];
    const imageMimeTypes = [];

    for (let index = 0; index < imageList.length; index += 1) {
        const image = imageList[index];
        const idCampaign = image.idCampaign || campaignItem.idCampaign;
        const response = yield call(campaigns.fetchCampaignImage, { idImage: image.idImage, idCampaign });

        if (response && response.status !== 304) {
            const { data } = response.data;
            if (data && data.image && data.image.content) {
                const base64Image = normalizeBase64(data.image.content);
                if (isValidBase64(base64Image)) {
                    const imageMimeType = sanitizeImageMimeType(data.image.contentType);
                    const imageUrl = createLocalImageUrl(base64Image, imageMimeType);
                    imageUrls.push(imageUrl);
                    imageMimeTypes.push(imageMimeType);
                }
            }
        }
    }

    if (imageUrls.length === 0) {
        return null;
    }

    return {
        ...campaignItem,
        imageUrl: imageUrls[0],
        imageUrls,
        imageMimeType: imageMimeTypes[0],
        imageMimeTypes,
    };
}

function* loadCampaignImage({ item }) {
    try {
        const image = getFittingImage(item.imageList);
        if (!image) {
            yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
        } else {
            const { idImage, idCampaign } = image;
            const response = yield call(campaigns.fetchCampaignImage, { idImage, idCampaign });

            if (response.status !== 304) {
                const { data } = response.data;
                yield put({ type: types.LOAD_CAMPAIGN_SUCCESS, item: { ...item, image: data.image.content } });
            }
        }
    } catch (err) {
        yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
    }
}

function* loadCampaignImages({ item }) {
    try {
        const fittingImageDisplay = getFittingImageDisplay();
        const preparedItem = yield call(prepareCampaignWithImages, item, fittingImageDisplay);
        if (!preparedItem) {
            yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
            return;
        }

        const currentItems = yield select((state) => state.campaigns.items || []);
        currentItems.forEach((campaignItem) => revokeCampaignItemResources(campaignItem));

        yield put({
            type: types.LOAD_CAMPAIGN_SUCCESS,
            item: preparedItem,
            items: [preparedItem],
        });
    } catch (err) {
        yield put({ type: types.LOAD_CAMPAIGN_FAILURE });
    }
}

function* dismissPermanentCampaign(idCampaign) {
    try {
        const response = yield call(campaigns.dismissPermanentCampaign, idCampaign);

        if (response.data.code !== "COR000I") {
            yield put({ type: types.DISMISS_CAMPAIGN_PERMANENT_SUCCESS });
        } else {
            yield put({ type: types.DISMISS_CAMPAIGN_PERMANENT_FAILURE });
        }
    } catch (err) {
        yield put({ type: types.DISMISS_CAMPAIGN_PERMANENT_FAILURE });
    }
}

function* removeCampaigns() {
    const currentItems = yield select((state) => state.campaigns.items || []);
    currentItems.forEach((item) => revokeCampaignItemResources(item));
}
